import * as React from "react";
import {
  CreateInstitutionWithAddresses,
  InstitutionWithAddresses,
} from "../../types/institution.types";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { PlusIcon } from "@heroicons/react/24/outline";
import { AddressForm } from "../../components/address-form";
import { EditableField } from "../../components/editable-field";
import { ErrorMessage } from "../../components/error-message";
import { Button } from "../../components/button";
import { PHONE_NUMBER_ARGENTINA_PATTERN } from "../../utils/patterns";

export type IInstitutionsFormProps =
  | {
      onSubmit: (data: InstitutionWithAddresses) => Promise<void>;
      handleInstitutionChange: (e: any) => void;
      handleAddressChange: (e: any) => void;
      addAddress?: () => void;
      removeAddress: (index: number) => void;
      handleCancel: () => void;
      editingInstitution: InstitutionWithAddresses;
      creatingInstitution?: never;
      isSubmitting: boolean;
    }
  | {
      onSubmit: (data: CreateInstitutionWithAddresses) => Promise<void>;
      handleInstitutionChange: (e: any) => void;
      handleAddressChange: (e: any) => void;
      addAddress: () => void;
      removeAddress: (index: number) => void;
      handleCancel: () => void;
      editingInstitution?: never;
      creatingInstitution: CreateInstitutionWithAddresses;
      isSubmitting: boolean;
    };

export default function InstitutionsForm({
  onSubmit,
  editingInstitution,
  creatingInstitution,
  handleInstitutionChange,
  handleAddressChange,
  addAddress,
  removeAddress,
  handleCancel,
  isSubmitting,
}: IInstitutionsFormProps) {
  const isEditMode = !!editingInstitution;
  const { error } = useErrorHandler();

  const currentInstitution = isEditMode
    ? editingInstitution
    : creatingInstitution;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isEditMode && editingInstitution) {
      await onSubmit(editingInstitution);
    } else if (!isEditMode && creatingInstitution) {
      await onSubmit(creatingInstitution);
    }
  };

  return (
    <div className="p-4 border border-gray-300 rounded-md">
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        {isEditMode ? "Editar barra pesquera" : "Crear nueva barra pesquera"}
      </h3>
      {error && <ErrorMessage message={error} />}

      {currentInstitution ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Institution */}
          <EditableField
            label="Nombre"
            value={currentInstitution.institution.name}
            onChange={(e) => handleInstitutionChange(e)}
            required
            type="text"
            minLength={3}
            maxLength={100}
            name="name"
            placeholder="Ingrese el nombre de la barra pesquera"
          />
          <EditableField
            label="Presidente"
            value={currentInstitution.institution.president}
            onChange={(e) => handleInstitutionChange(e)}
            type="text"
            minLength={3}
            maxLength={250}
            name="president"
            placeholder="Ingrese el nombre del presidente"
          />
          <EditableField
            label="Teléfono"
            value={currentInstitution.institution.phone_number}
            onChange={(e) => handleInstitutionChange(e)}
            type="text"
            pattern={PHONE_NUMBER_ARGENTINA_PATTERN}
            minLength={3}
            maxLength={250}
            name="phone_number"
            placeholder="Ingrese el número de teléfono"
          />
          {/* Address */}
          {isEditMode && currentInstitution.addresses.length === 0 ? (
            <></>
          ) : (
            <div>
              <div className="mt-4 mb-4">
                <h4 className="text-lg font-bold text-gray-800 mb-2">
                  Direcciones
                </h4>
                <AddressForm
                  addresses={currentInstitution.addresses}
                  onChange={handleAddressChange}
                  onRemove={removeAddress}
                  showRemoveButton={!isEditMode}
                />
              </div>
              {addAddress && (
                <button
                  onClick={addAddress}
                  className="flex items-center px-3 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition"
                  type="button"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Agregar Dirección
                </button>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2 mt-4">
            <Button
              type="button"
              text="Cancelar"
              variant="cancel"
              onClick={handleCancel}
            />
            <Button
              type="submit"
              text={isEditMode ? "Guardar Cambios" : "Crear Barra Pesquera"}
              variant={isEditMode ? "edit" : "create"}
              disabled={isSubmitting}
            />
          </div>
        </form>
      ) : null}
    </div>
  );
}
